import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type TextElement = 'p' | 'span' | 'div';

const headingVariants = cva('text-balance font-semibold tracking-tight text-foreground', {
    variants: {
        variant: {
            page: 'text-2xl font-bold sm:text-4xl',
            section: 'text-xl font-bold leading-normal',
            subsection: 'text-lg',
            card: 'text-base leading-snug sm:text-lg',
            empty: 'text-lg',
        },
    },
    defaultVariants: {
        variant: 'section',
    },
});

const textVariants = cva('text-pretty', {
    variants: {
        variant: {
            body: 'text-sm leading-relaxed text-foreground',
            description: 'text-sm leading-relaxed text-muted-foreground',
            subtitle: 'max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base',
            caption: 'text-xs leading-relaxed text-muted-foreground',
            label: 'text-xs font-medium text-muted-foreground',
            meta: 'text-sm font-medium text-muted-foreground',
        },
    },
    defaultVariants: {
        variant: 'body',
    },
});

const DEFAULT_HEADING_TAG: Record<
    NonNullable<VariantProps<typeof headingVariants>['variant']>,
    HeadingElement
> = {
    page: 'h1',
    section: 'h2',
    subsection: 'h3',
    card: 'h3',
    empty: 'h3',
};

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement> &
    VariantProps<typeof headingVariants> & {
        as?: HeadingElement;
    };

export function Heading({ as, variant = 'section', className, ...props }: HeadingProps) {
    const Component = as ?? DEFAULT_HEADING_TAG[variant ?? 'section'];

    return <Component className={cn(headingVariants({ variant }), className)} {...props} />;
}

type TextProps = React.HTMLAttributes<HTMLElement> &
    VariantProps<typeof textVariants> & {
        as?: TextElement;
    };

export function Text({ as: Component = 'p', variant = 'body', className, ...props }: TextProps) {
    return <Component className={cn(textVariants({ variant }), className)} {...props} />;
}

type EyebrowProps = React.HTMLAttributes<HTMLParagraphElement>;

export function Eyebrow({ className, ...props }: EyebrowProps) {
    return (
        <p
            className={cn(
                'text-xs font-semibold uppercase tracking-[0.14em] text-brand/80',
                className,
            )}
            {...props}
        />
    );
}
